import {GalleryImageItem} from "@oebb-web-team/oebb-web-components";
import {CompartmentImage, TrainInformation} from "../../services/train-information.service";
import {doesPassengerRequireWheelchair} from "../../utils/offers";
import {AccommodationType} from "./AccommodationType";
import { i18n } from '../../utils/I18n';

export const GALLERY_HEIGHT = 191; // px
export const MOBILE_DEVICE_WIDTH = 768; // px

export const IMAGE_DEFAULT_WIDTH = 307;
export const IMAGE_WIDTHS = [307, 800, 1200, 1920, 3840];

export function getCompartmentInformation(data: TrainInformation, accommodationType: AccommodationType) {
	if (!data) {
		return null;
	}

	if (doesPassengerRequireWheelchair()) {
		return data.wheelchair;
	}

	switch (accommodationType) {
		case AccommodationType.Se:
			return data.seat;
		case AccommodationType.Le:
			return data.couchette;
		case AccommodationType.Be:
			return data.sleeper;
	}
}

function buildImageSrcForWidth(image: CompartmentImage, width: number): string {
	return image?.image?.replace('{width}', width.toString());
}

function buildImageSrcSet(image: CompartmentImage, widths: number[]): string {
	if (!image?.image || !widths?.length) {
		return null;
	}

	// Collect all widths that are below the native image width (if provided)
	const usedWidths = image.width ? widths?.filter(width => width < image.width) : widths;

	if (image.width && image.width <= widths[widths.length - 1]) {
		// Provide the native size if a higher one is requested
		usedWidths.push(image.width);
	}

	return usedWidths.map(width => buildImageSrcForWidth(image, width) + ' ' + width + 'w').join(', ');
}

export function buildGalleryItems(images: CompartmentImage[], defaultWidth: number, widths: number[]): GalleryImageItem[] {
	return images?.map(image => {
		const copyright = image.copyright && !image.copyright.startsWith('&copy;') && !image.copyright.startsWith('©')
			? `© ${image.copyright}`
			: image.copyright;
		const title = image.title && copyright
			? `${image.title} ${copyright}`
			: (image.title || copyright);
		return {
			type: 'image',
			imageSrc: buildImageSrcForWidth(image, defaultWidth),
			imageSrcSet: buildImageSrcSet(image, widths),
			title,
			alt: image.caption,
			caption: image.caption,
			copyright: title,
		};
	}) ?? [];
}

export function getCompartmentTitle(accommodationType: AccommodationType) {
    switch (accommodationType) {
      case AccommodationType.Se: return i18n.getMessage("ts.accommodation.SE");
      case AccommodationType.Le: return i18n.getMessage("ts.accommodation.LE");
      case AccommodationType.Be: return i18n.getMessage("ts.accommodation.BE");
    }
}

export function getCompartmentBookingInformationList(accommodationType: AccommodationType, hidePrivateCompartment = false): string[] {
  switch (accommodationType) {
    case AccommodationType.Se:
      const messagesSeatingCarriage = ([
        i18n.getMessage("reservation.offer-list.seating-car.booking-information.front"),
	  ]);
	  if (!hidePrivateCompartment) {
		  messagesSeatingCarriage.push(i18n.getMessage("reservation.offer-list.seating-car.booking-information.link-text"));
	  }
		messagesSeatingCarriage.push(i18n.getMessage("reservation.offer-list.seating-car.booking-information.trailer"));
	  return messagesSeatingCarriage;
    case AccommodationType.Le:
		const messagesCouchetteCarriage =  ([
        	i18n.getMessage("reservation.offer-list.couchette.booking-information.front"),
      	]);
		if (!hidePrivateCompartment) {
			messagesCouchetteCarriage.push(i18n.getMessage("reservation.offer-list.couchette.booking-information.link-text"));
		}
		messagesCouchetteCarriage.push(i18n.getMessage("reservation.offer-list.couchette.booking-information.trailer"));
		return messagesCouchetteCarriage;
    case AccommodationType.Be:
		const messagesSleepingCarriage =  ([
			i18n.getMessage("reservation.offer-list.sleepers.booking-information.front"),
		]);
		if (!hidePrivateCompartment) {
			messagesSleepingCarriage.push(i18n.getMessage("reservation.offer-list.sleepers.booking-information.link-text"));
		}
		messagesSleepingCarriage.push(i18n.getMessage("reservation.offer-list.sleepers.booking-information.trailer"));
		return messagesSleepingCarriage;
  }
}
